import type { ItemId } from "../content/items";
import "./ItemArt.css";

/*
  The Dojo Shop's items, drawn in the ninja art's chunky style: bold shapes,
  dark outlines, one strong colour each, on a 100 × 100 canvas. Every item is
  a config entry (content/items.ts) plus a drawing here, so a new item is a
  row and a small picture.
*/

const INK = "#1d2340";
const W = 4;

/** The drawing for one item. Level pips are drawn by the card, not here. */
export function ItemArt({
  id,
  className,
  dim = false,
}: {
  id: ItemId;
  className?: string | undefined;
  /** Greyed, for an item that's used, locked or not owned. */
  dim?: boolean;
}) {
  return (
    <span className={["item-art", dim ? "dim" : "", className].filter(Boolean).join(" ")}>
      <svg viewBox="0 0 100 100" aria-hidden="true">
        {ART[id]}
      </svg>
    </span>
  );
}

const ART: Record<ItemId, React.ReactNode> = {
  "slow-time": (
    <g stroke={INK} strokeWidth={W} strokeLinecap="round" strokeLinejoin="round">
      {/* an hourglass with sand held mid-fall */}
      <path d="M28 14h44M28 86h44" />
      <path
        d="M32 14c0 20 14 26 18 36-4 10-18 16-18 36M68 14c0 20-14 26-18 36 4 10 18 16 18 36"
        fill="#cfe8ff"
      />
      <path d="M38 22c4 10 8 16 12 20 4-4 8-10 12-20Z" fill="#f5b82e" stroke="none" />
      <path d="M36 82c2-14 8-20 14-24 6 4 12 10 14 24Z" fill="#f5b82e" stroke="none" />
      <circle cx="50" cy="50" r="3" fill="#f5b82e" stroke="none" />
      <path d="M78 34l10-6M78 66l10 6" />
    </g>
  ),
  "smoke-bomb": (
    <g stroke={INK} strokeWidth={W} strokeLinecap="round" strokeLinejoin="round">
      {/* a round bomb with a puff of smoke */}
      <circle cx="46" cy="60" r="26" fill="#4a5378" />
      <path d="M52 32l8-12" />
      <circle cx="62" cy="18" r="4" fill="#f5b82e" />
      <path d="M62 44c14-8 26-2 26 8s-10 14-18 10" fill="#e6ecf5" />
      <path d="M36 50c-6 8-4 18 4 22" stroke="#e6ecf5" strokeWidth="5" />
    </g>
  ),
  "double-strike": (
    <g stroke={INK} strokeWidth={W} strokeLinecap="round" strokeLinejoin="round">
      {/* two crossed blades */}
      <path d="M22 78L70 30" stroke="#d3dae5" strokeWidth="12" />
      <path d="M78 78L30 30" stroke="#d3dae5" strokeWidth="12" />
      <path d="M22 78L70 30M78 78L30 30" />
      <path d="M18 74l8 8M82 74l-8 8" strokeWidth="7" stroke="#d9483b" />
      <path d="M70 30l8-8M30 30l-8-8" strokeWidth="6" />
    </g>
  ),
  "shield-scroll": (
    <g stroke={INK} strokeWidth={W} strokeLinecap="round" strokeLinejoin="round">
      {/* a shield with a scroll's curl at the top */}
      <path d="M50 14l30 10v26c0 20-14 32-30 38-16-6-30-18-30-38V24Z" fill="#3e7cb1" />
      <path d="M50 22l20 8v20c0 14-9 24-20 29-11-5-20-15-20-29V30Z" fill="#cfe8ff" stroke="none" />
      <path d="M40 50l8 8 14-16" strokeWidth="6" />
    </g>
  ),
  "hint-scroll": (
    <g stroke={INK} strokeWidth={W} strokeLinecap="round" strokeLinejoin="round">
      {/* an unrolled scroll with a number line on it */}
      <rect x="22" y="26" width="56" height="48" rx="6" fill="#fff3d6" />
      <circle cx="22" cy="26" r="7" fill="#f5b82e" />
      <circle cx="78" cy="26" r="7" fill="#f5b82e" />
      <circle cx="22" cy="74" r="7" fill="#f5b82e" />
      <circle cx="78" cy="74" r="7" fill="#f5b82e" />
      <path d="M32 50h36M36 44v12M50 44v12M64 44v12" />
      <circle cx="50" cy="50" r="4" fill="#d9483b" stroke="none" />
    </g>
  ),
  "swift-sandals": (
    <g stroke={INK} strokeWidth={W} strokeLinecap="round" strokeLinejoin="round">
      {/* a sandal with speed lines */}
      <path
        d="M30 66c-6 0-8-8-2-12l12-6c8-4 12-14 22-14h14c8 0 10 8 6 14l-6 10c-4 6-8 8-16 8Z"
        fill="#2f8f5b"
      />
      <path d="M44 48l14-6M40 60l14-6" stroke="#f5b82e" strokeWidth="5" />
      <path d="M12 34h14M8 46h12M12 58h10" />
    </g>
  ),
  "iron-headband": (
    <g stroke={INK} strokeWidth={W} strokeLinecap="round" strokeLinejoin="round">
      {/* a headband with an iron plate */}
      <path d="M12 46c10-8 66-8 76 0v14c-10 8-66 8-76 0Z" fill="#4a5378" />
      <rect x="36" y="40" width="28" height="24" rx="5" fill="#d3dae5" />
      <circle cx="42" cy="46" r="2" fill={INK} stroke="none" />
      <circle cx="58" cy="46" r="2" fill={INK} stroke="none" />
      <circle cx="42" cy="58" r="2" fill={INK} stroke="none" />
      <circle cx="58" cy="58" r="2" fill={INK} stroke="none" />
      <path d="M12 60l-4 20 12-8M88 60l4 20-12-8" fill="#4a5378" />
    </g>
  ),
  "lucky-charm": (
    <g stroke={INK} strokeWidth={W} strokeLinecap="round" strokeLinejoin="round">
      {/* a four-leaf charm on a cord */}
      <path d="M50 60v26" />
      <path
        d="M50 50c-14-2-22-8-22-16s8-10 14-4c-2-8 0-16 8-16s10 8 8 16c6-6 14-4 14 4s-8 14-22 16Z"
        fill="#2f8f5b"
      />
      <path d="M50 50c2-14 8-22 16-22s10 8 4 14c8-2 16 0 16 8s-8 10-16 8" fill="#2f8f5b" />
      <circle cx="50" cy="48" r="5" fill="#f5b82e" />
      <path d="M22 20l3 6M78 20l-3 6" stroke="#f5b82e" strokeWidth="5" />
    </g>
  ),
  "focus-beads": (
    <g stroke={INK} strokeWidth={W} strokeLinecap="round" strokeLinejoin="round">
      {/* a loop of beads */}
      <circle
        cx="50"
        cy="52"
        r="28"
        fill="none"
        strokeDasharray="0.1 12"
        strokeWidth="11"
        strokeLinecap="round"
        stroke="#8e5bd0"
      />
      <circle
        cx="50"
        cy="52"
        r="28"
        fill="none"
        strokeDasharray="0.1 12"
        strokeWidth="5"
        strokeLinecap="round"
        stroke={INK}
        opacity="0.35"
      />
      <circle cx="50" cy="16" r="8" fill="#f5b82e" />
      <path d="M50 24v6" />
    </g>
  ),
};
